import { z } from 'zod';
import { languageCodeSchema, languageCodeValues } from './facility';
import { inboxThreadDerivedStatusEnum } from './inbox-threads';
import {
  itineraryProcessStepSchema,
  itineraryProcessTargetsSchema,
  travellerPreviewAccessSchema,
} from './itinerary-search';

// Wire shapes for `itineraries.byId`. JSONB columns stay `z.unknown()` —
// renderers narrow as needed.

// Item review status (XT-460, 28.07 review meeting). Wire-only enum —
// storage is Main `customer_preview_conversation.discussion_status`.
//   null       Ready — the customer has not acted on this service yet
//   approved   the customer approved it; the service is locked
//   in-review  the customer withdrew an approval they had given
//   rejected   the customer declined it — out of pricing, flagged for the
//              consultant to remove from the itinerary
export const itemClientStatusSchema = z.enum([
  'ready',
  'in-review',
  'approved',
  'rejected',
]);

export type ItemClientStatus = z.infer<typeof itemClientStatusSchema>;

// Phase 03 #258 / PRD FR-2.1 — read-only projection of the inbox thread
// linked to this itinerary via `inbox.thread.itinerary_id`. Joined server-
// side from `crm.v_customer` so the FE doesn't need a follow-up lookup.
// `lastReplyAt` is the timestamp of the most recent customer message
// (mirrors `inbox.thread.last_paste_at`); `pastedMessagesCount` is the
// total number of `pasted-customer` messages on the thread (drives the
// "{n} messages" pill in the Conversation Summary widget). `startedAt`
// is the thread's creation timestamp (`inbox.thread.created_on`, always
// present) — it drives the "Started {date}" prefix in the widget's
// provenance line (PRD US-3, pen `DcEL8`).
export const itineraryThreadSummarySchema = z.object({
  threadId: z.uuid(),
  customerName: z.string(),
  status: z.string(),
  startedAt: z.iso.datetime({ offset: true }),
  // Reuses the inbox's `inboxThreadDerivedStatusEnum` directly so
  // adding a status on the inbox side is a single-source update. The
  // union is the seven `inbox.thread.status` values plus the derived
  // `'stalled'` value the inbox list computes.
  derivedStatus: inboxThreadDerivedStatusEnum,
  lastReplyAt: z.iso.datetime({ offset: true }).nullable(),
  pastedMessagesCount: z.number().int().nonnegative(),
  subjectLine: z.string().nullable(),
});

export type ItineraryThreadSummary = z.infer<
  typeof itineraryThreadSummarySchema
>;

// Read-side sign-off meter on `itineraries.byId` (Main discussion_status).
// XT-460 (28.07): every service is in scope.
//   approved / confirmed — count of `approved`
//   clarifying           — count of `in-review` (withdrawn approvals)
//   total                — all root items (null/ready still inflate total only)
export const itineraryClientStatusTallySchema = z.object({
  approved: z.number().int().nonnegative(),
  clarifying: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
});

export type ItineraryClientStatusTally = z.infer<
  typeof itineraryClientStatusTallySchema
>;

export const itineraryItemBaseSchema = z.object({
  id: z.uuid(),
  productId: z.uuid().nullable(),
  itineraryVersionId: z.uuid().nullable(),
  parentId: z.uuid().nullable(),
  title: z.unknown(),
  description: z.unknown(),
  thumbnail: z.string().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  currency: z.string().nullable(),
  price: z.unknown(),
  travellers: z.unknown(),
  legs: z.unknown(),
  details: z.unknown(),
  tags: z.unknown(),
  facilities: z.unknown(),
  suppliers: z.unknown(),
  attachments: z.unknown(),
  videos: z.unknown(),
  notes: z.string().nullable(),
  customerNotes: z.string().nullable(),
  privateNotes: z.string().nullable(),
  isOptional: z.boolean().nullable(),
  isPreviewHidden: z.boolean().nullable(),
  isLine: z.boolean().nullable(),
  isPricingHidden: z.boolean().nullable(),
  pricingType: z.string().nullable(),
  pricingUnits: z.number().int().nullable(),
  isGross: z.boolean().nullable(),
  // Group items keep every rate here, not in `price`. Read-only on the item
  // surface — `itineraryItemPatch` deliberately does NOT accept it, so the
  // Pricing tab can render the thresholds snapshotted from the product
  // without making them editable yet (XT-631).
  groupPriceThresholds: z.unknown().optional(),
  stars: z.string().nullable(),
  catalogId: z.uuid().nullable(),
  operationStatusId: z.uuid().nullable(),
  transportType: z.string().nullable(),
  serviceType: z.string().nullable(),
  // Cost / sell VAT ids — read by the subitem form (XT-837).
  costVatId: z.uuid().nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
  optionalItems: z.unknown(),
  createdOn: z.string(),
  // Operations-read fields (Phase 11 #48e). Written by the operations
  // app; read here so the planner can render colour-codes, attention
  // pills, and driver/guide popovers. Planner mutations must NEVER
  // touch these — they're owned by `operations:update`.
  tourOperators: z.unknown(),
  attentionCategory: z.string().nullable(),
  attentionNote: z.string().nullable(),
  operationDriverNotes: z.string().nullable(),
  operationInternalNote: z.string().nullable(),
  operationSupplierReference: z.string().nullable(),
  // Derived flags (Phase 11 #48f). Pure server-side derivation from
  // contentCategory + start/end. Drive Phase 14 DnD guards (accommodation
  // check-out can't precede check-in; rental drop-off can't precede
  // pickup) and Phase 17 row time-pill labels.
  isCheckIn: z.boolean(),
  isCheckOut: z.boolean(),
  pickup: z.boolean(),
  isDropOff: z.boolean(),
  // Chapter assignment (Phase 11 #48g). Pre-computed server-side via the
  // chapter-span match `sections[i].startDate <= startTime <= sections[i].endDate`.
  // Drives the Phase 19 chapter filter cascade without a per-render join.
  chapterId: z.string().nullable(),
  // Item review status (XT-460, 28.07). Wire: ready | in-review | approved |
  // rejected. `null` = Ready (not yet set). Merged into byId from Main
  // `customer_preview_conversation.discussion_status`. Read-only for CON:
  // writer is CUS via token-gated `customerPreviewAccess.setItemStatus`.
  clientStatus: itemClientStatusSchema.nullable(),
  // Note paired with `clientStatus` (Main `discussion_note`). Currently
  // stamped null on public Accept/Reject; kept for timeline round-trip.
  clientNote: z.string().nullable(),
});

export type ItineraryItemBase = z.infer<typeof itineraryItemBaseSchema>;

// Adds nested children: subitems are returned with the base schema (no recursive nesting).
export const itineraryItemSchema = itineraryItemBaseSchema.extend({
  subItems: z.array(itineraryItemBaseSchema),
  subItemCount: z.number().int().nonnegative(),
  optionalItemDetails: z.array(itineraryItemBaseSchema),
  // Phase 11 #48h. Mirrors `subItemCount` — FE renders "+N optional
  // items" pill without counting `optionalItemDetails` client-side.
  optionalItemCount: z.number().int().nonnegative(),
});

export type ItineraryItem = z.infer<typeof itineraryItemSchema>;

export const itineraryVersionSchema = z.object({
  id: z.uuid(),
  itineraryId: z.uuid().nullable(),
  versionTitle: z.string(),
  startTime: z.string().nullable(),
  finishTime: z.string().nullable(),
  durationDays: z.number().int().nullable(),
  budget: z.string().nullable(),
  price: z.string().nullable(),
  priceEstimate: z.string().nullable(),
  // Billing-and-priority columns the settings form binds against —
  // exposed read-side so the page can seed its form state from byId.
  paymentZoneId: z.uuid().nullable(),
  termsAndConditionsId: z.uuid().nullable(),
  accountingReference: z.string().nullable(),
  travellers: z.unknown(),
  // Free-form per-traveller contact info — legacy stores `string[]` of
  // `name:email:phone` triples. The settings form parses/recombines.
  travellersContactInfo: z.array(z.string()).nullable(),
  exchangeRates: z.unknown(),
  // Projected wire shape: flat `string[]` derived server-side from the rich
  // `day_titles` jsonb (`DayTitleDto[]`). Subtitles / descriptions / AI
  // flags are parallel arrays computed in the same projection pass — no
  // separate columns back them.
  dayTitles: z.array(z.string()).nullable(),
  daySubtitles: z.array(z.string()).nullable(),
  dayDescriptions: z.array(z.string()).nullable(),
  dayTitlesAiGenerated: z.array(z.boolean()).nullable(),
  hasAiGeneratedTitles: z.boolean(),
  dayImages: z.unknown(),
  attachments: z.unknown(),
  // `chapterSchema[]` jsonb — FE narrows when reading.
  sections: z.unknown(),
  modifiedOn: z.string().nullable(),
  createdOn: z.string().nullable(),
});

export type ItineraryVersion = z.infer<typeof itineraryVersionSchema>;

export const itineraryStatusSchema = z.object({
  id: z.uuid(),
  status: z.string().nullable(),
  // T&C status name — the "ongoing" check matches this (e.g. "A DECORRER")
  // even when the display `status` is prefixed (e.g. "O2: Ongoing (A DECORRER)").
  tcStatus: z.string().nullable(),
  color: z.string().nullable(),
  isCompleted: z.number().int().nullable(),
  // Drives the Final Client Details Form gate (band 7..19). PG `numeric`, so
  // it arrives as a string.
  salesStage: z.union([z.number(), z.string()]).nullable().optional(),
  // XT-264's Operations-scope membership — true from "RESERVAS
  // FORNECEDORES/OPTITRAVEL" onwards in a stock catalog. Gates the planner's
  // per-row operation-status control (XT-702); read it instead of banding
  // `salesStage`, which the funnel is free to re-stage.
  isOperational: z.boolean().default(false),
});

export type ItineraryStatus = z.infer<typeof itineraryStatusSchema>;

// Status option for the header transition selector (Phase 15a #55a).
// Includes `salesStage` so the FE can filter by `TRANSITIONS[fromStage]`
// without a second round-trip. `salesStage` is the numeric column
// stored as text in PG — already parseable via `parseSalesStage`.
export const itineraryStatusOptionSchema = z.object({
  id: z.uuid(),
  status: z.string().nullable(),
  color: z.string().nullable(),
  salesStage: z.number().int().nullable(),
  // XT-521 — org-configured version-management lock. When true, an itinerary
  // in this status cannot have its active version changed and its non-active
  // versions are not editable. Carried on the option (not just the itinerary's
  // own status shape) because the FE lock mirror resolves it from this catalog.
  activeVersionLocked: z.boolean(),
});

// XT-158 — per-itinerary document format settings driven by end-user
// criteria. Stored as NOT NULL text columns on `itineraries.itinerary`
// (defaults: 24h / full / km) and surfaced to the preview renderers so the
// presented information (times, dates, distances) adapts to the end user.
export const hourFormatSchema = z.enum(['24h', '12h']);
export const dateFormatSchema = z.enum(['full', 'long', 'medium', 'short']);
export const distanceUnitSchema = z.enum(['km', 'miles']);
export type HourFormat = z.infer<typeof hourFormatSchema>;
export type DateFormat = z.infer<typeof dateFormatSchema>;
export type DistanceUnit = z.infer<typeof distanceUnitSchema>;

// XT-692 — the itinerary's storefront (CMS) title, one entry per language,
// stored as `itinerary.cms_title` jsonb in the same `[{ languageCode, text }]`
// shape as a product title. Null = never edited; readers then derive it from
// the plain title via `resolveItineraryCmsTitle` (`@travel-planner/shared/utils`).
export const itineraryCmsTitleEntrySchema = z.object({
  languageCode: languageCodeSchema,
  text: z.string().trim().min(1).max(256),
});

export const itineraryCmsTitleSchema = z
  .array(itineraryCmsTitleEntrySchema)
  .max(languageCodeValues.length)
  .refine(
    (entries) =>
      new Set(entries.map((e) => e.languageCode)).size === entries.length,
    { message: 'One CMS title per language' }
  );

export type ItineraryCmsTitle = z.infer<typeof itineraryCmsTitleSchema>;

// The storefront's 12 locales — the targets of "Translate missing languages".
// Keep in sync with `apps/cms-public/web/src/shared/lib/get-tenant-context.ts`.
export const storefrontLanguageCodes = [
  'en',
  'pt',
  'es',
  'fr',
  'zh',
  'ar',
  'it',
  'de',
  'ja',
  'hi',
  'ru',
  'ko',
] as const satisfies readonly (typeof languageCodeValues)[number][];

// Translates one CMS title into the requested languages. Stateless — the tab
// sends its unsaved source text, so nothing is read or written.
export const translateItineraryCmsTitleInput = z
  .object({
    sourceLanguage: languageCodeSchema,
    text: z.string().trim().min(1).max(256),
    targetLanguages: z
      .array(languageCodeSchema)
      .min(1)
      .max(languageCodeValues.length),
  })
  .refine((input) => !input.targetLanguages.includes(input.sourceLanguage), {
    message: 'Target languages must differ from the source language',
  });

export type TranslateItineraryCmsTitleInput = z.infer<
  typeof translateItineraryCmsTitleInput
>;

export const translateItineraryCmsTitleResult = z.object({
  entries: z.array(
    z.object({ languageCode: languageCodeSchema, text: z.string() })
  ),
});

export type TranslateItineraryCmsTitleResult = z.infer<
  typeof translateItineraryCmsTitleResult
>;

export const itinerarySchema = z.object({
  id: z.uuid(),
  itineraryRef: z.string().nullable(),
  title: z.string().nullable(),
  currency: z.string().nullable(),
  displayCurrency: z.string().nullable(),
  languageCode: z.string().nullable(),
  // XT-692 — raw column; null until the CMS Title tab saves one.
  cmsTitle: z
    .array(z.object({ languageCode: z.string(), text: z.string() }))
    .nullable(),
  // XT-158: NOT NULL columns (defaults applied at the DB), so always present.
  hourFormat: hourFormatSchema,
  dateFormat: dateFormatSchema,
  distanceUnit: distanceUnitSchema,
  statusId: z.uuid().nullable(),
  isLocked: z.boolean(),
  undated: z.boolean().nullable(),
  note: z.string().nullable(),
  privateNotes: z.string().nullable(),
  // Per-itinerary supplier notes (legacy `all_suppliers_notes`). Surfaced
  // in the WhatsApp message variables; the settings page exposes it as the
  // third rich-text note alongside `note` / `privateNotes`.
  allSuppliersNotes: z.string().nullable(),
  // Free-text reason captured when the itinerary is moved into a CANCELLED
  // status (legacy `cancellationReason`). Required at cancel time on the FE;
  // null for itineraries that were never cancelled.
  cancellationReason: z.string().nullable(),
  tags: z.unknown(),
  hiddenHolidays: z.unknown(),
  // Itinerary's primary destination country — enum on the DB (legacy
  // `itinerary_country_enum`). Drives the holidays match for the
  // per-day badge (Phase 15a #55d).
  country: z.string().nullable(),
  contactId: z.uuid().nullable(),
  // Display name of the itinerary's customer, joined from crm.v_customer by
  // contactId WITHOUT an org filter — a customer attached to this itinerary
  // may live in another org. Lets the settings form show the selected
  // customer even when the org-scoped `customers.byId` lookup can't resolve
  // it (parity with the list, which joins the name the same way).
  contactName: z.string().nullable(),
  accountId: z.uuid().nullable(),
  organizationId: z.uuid(),
  appearanceId: z.uuid().nullable(),
  // Drives the hero-video shown in the customer preview. Picked from
  // the regional-videos catalog by (country, region); see 55h-i.
  regionalVideoId: z.uuid().nullable(),
  activeVersionId: z.uuid().nullable(),
  // Toggles the customer-preview budget breakdown surface (legacy parity).
  showBudgetBreakdownInPreview: z.boolean().nullable(),
  // Customer-preview publish window. `previewPublishTime` is set when the
  // consultant publishes; `previewUnpublishTime` is the optional scheduled
  // expiry. Read-side preview access is gated by `disabled` — these are
  // primarily bookkeeping + display ("expires in N days").
  previewPublishTime: z.string().nullable(),
  previewUnpublishTime: z.string().nullable(),
  // Set by the server when the status transitions into SOLD; the
  // settings form lets consultants override it manually (legacy parity).
  soldOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  createdOn: z.string(),
  deletedOn: z.string().nullable(),
});

export type Itinerary = z.infer<typeof itinerarySchema>;

export const itineraryDayHolidaySchema = z.object({
  name: z.string(),
  // ISO-date prefix (YYYY-MM-DD) — same shape `hiddenHolidays` stores.
  date: z.string(),
  // Mirrors whether `date` is in `itinerary.hiddenHolidays`. The FE
  // uses this to flip the menu label between "Hide holiday" / "Show
  // holiday" without re-checking the array client-side.
  hidden: z.boolean(),
});

export type ItineraryDayHoliday = z.infer<typeof itineraryDayHolidaySchema>;

export const itineraryDaySchema = z.object({
  index: z.number().int().nonnegative(),
  date: z.string().nullable(),
  title: z.string().nullable(),
  // Secondary line under the day pill. Legacy parity: when the
  // consultant set a custom title (e.g. "Lisbon → Porto"), `subtitle`
  // carries the formatted date (`"Wed 22. Apr 2026"`) or `"Day N"` for
  // undated itineraries. When no custom title was set, the pill itself
  // already shows the date / `"Day N"` and `subtitle` is null.
  subtitle: z.string().nullable(),
  imageUrl: z.string().nullable(),
  items: z.array(itineraryItemSchema),
  // Phase 15a #55d — holidays the BE matched against the day's date,
  // already filtered by `hiddenHolidays`. Empty array when no match.
  holidays: z.array(itineraryDayHolidaySchema),
});

export type ItineraryDay = z.infer<typeof itineraryDaySchema>;

// Itinerary chaining (legacy parity). A neighbour in the chain, resolved
// with its title for display. Stored on the DB as a `chain` jsonb holding
// only the uuids; the read paths resolve titles. A soft-deleted /
// cross-org / disabled neighbour resolves to `null`.
export const itineraryChainNeighborSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  // Does this neighbour live in the SAME organization as the itinerary whose
  // chain this is? A chain may span partner DMCs (XT-710), and the two hops
  // need different preview modes: a consultant can open a same-org leg in the
  // consultant view, but `preview.byId` is org-scoped, so a cross-org leg only
  // resolves on the public traveller view. The nav arrow reads this to pick
  // `?view=`, which is what keeps a consultant off "Preview not found".
  // Computed against the SOURCE itinerary's org, not the caller's — on the
  // consultant path those are the same (byId is org-scoped), and on the public
  // path there is no caller org to compare with.
  sameOrganization: z.boolean(),
});

export type ItineraryChainNeighbor = z.infer<
  typeof itineraryChainNeighborSchema
>;

// The doubly-linked-list view of an itinerary's chain. `previous` is
// read-only at the API boundary (derived from the other itinerary's
// `next`); the consultant only sets `next`.
export const itineraryChainSchema = z.object({
  previous: itineraryChainNeighborSchema.nullable(),
  next: itineraryChainNeighborSchema.nullable(),
});

export type ItineraryChain = z.infer<typeof itineraryChainSchema>;

// `priceLines` are `isLine = true` rows surfaced for budget-bar parity.
export const itineraryDetailSchema = itinerarySchema.extend({
  status: itineraryStatusSchema.nullable(),
  activeVersion: itineraryVersionSchema.nullable(),
  days: z.array(itineraryDaySchema),
  priceLines: z.array(itineraryItemBaseSchema),
  // Phase 03 #258 — read-side join onto `inbox.thread.itinerary_id`.
  // Surfaces the linked customer-thread summary so the planner can render
  // the Conversation Summary widget (PRD FR-2.1). `null` when no thread
  // is linked. The itineraries domain never writes `inbox` — this is a
  // read-only projection folded into the existing `byId` query path.
  thread: itineraryThreadSummarySchema.nullable(),
  // Phase 03 #258 / XT-383 — derived client-status tally. Computed from
  // CMS-merged item statuses at byId read time; excludes `is_line` and
  // `is_optional` rows; never persisted (G3).
  clientStatusTally: itineraryClientStatusTallySchema,
  // Itinerary chaining — the prev/next neighbours (with titles) resolved
  // from the `chain` jsonb. Both sides null when the itinerary is not
  // chained. Drives the settings-page "Itinerary Chain" section.
  chain: itineraryChainSchema,
});

export type ItineraryDetail = z.infer<typeof itineraryDetailSchema>;

export const itineraryByIdInput = z.object({
  id: z.uuid(),
  // true: flat (subitems as siblings, `subItems` cleared). false: nested.
  includeSubitems: z.boolean().default(false),
  // Read a specific (typically historical) version instead of the live
  // `active_version_id`. Powers the consultant's read-only "view a previous
  // version" mode — the sold/live version is never mutated or swapped.
  // Consultant-only: this field is deliberately absent from the public
  // traveller read (`previewByIdCustomerInput`), so OPS and clients always
  // see only the active version. A version belonging to another itinerary or
  // org fails to resolve → NOT_FOUND.
  versionId: z.uuid().optional(),
});

export type ItineraryByIdInput = z.infer<typeof itineraryByIdInput>;

// Explicit allow-list — never `Partial<itineraryItemSchema>`.
const hhmmSchema = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'Expected canonical HH:MM');

export const itineraryItemPatchSchema = z
  .object({
    // Multilingual jsonb (`[{ languageCode, text }]`). Edited from the
    // item-detail page's Title & Description tab; passes through
    // verbatim so the FE owns the localized-array shape.
    title: z.unknown().optional(),
    description: z.unknown().optional(),
    // External video URLs (string[]). Edited from the item-detail
    // page's Images & Videos tab. Image attachments are written via
    // the existing `attachments` field below.
    videos: z.array(z.string()).nullable().optional(),
    // Caller sends day + HH:MM; server resolves the UTC ISO.
    dayIndex: z.number().int().min(0).max(364).optional(),
    startTime: hhmmSchema.nullable().optional(),
    // Mirrors dayIndex/startTime for the end side of a multi-day row
    // (Phase 14 #56c — dragging the check-out / drop-off virtual entry).
    // Server resolves to a UTC `endTime` ISO + rewrites the `legs[0]`
    // jsonb so legacy reads the new dropoff time. Rejected if the
    // resulting `endTime` would precede `startTime`.
    endDayIndex: z.number().int().min(0).max(364).optional(),
    endTime: hhmmSchema.nullable().optional(),
    notes: z.string().max(10_000).nullable().optional(),
    customerNotes: z.string().max(10_000).nullable().optional(),
    privateNotes: z.string().max(10_000).nullable().optional(),
    price: z.unknown().optional(),
    pricingType: z.string().max(64).nullable().optional(),
    pricingUnits: z.number().int().nullable().optional(),
    isGross: z.boolean().nullable().optional(),
    currency: z.string().length(3).nullable().optional(),
    priceVatId: z.uuid().nullable().optional(),
    costVatId: z.uuid().nullable().optional(),
    // Triggers `travellersManuallySet = true` server-side.
    travellers: z.unknown().optional(),
    isPreviewHidden: z.boolean().nullable().optional(),
    isOptional: z.boolean().nullable().optional(),
    // Customer preview honours this on `is_optional` rows: when false /
    // null, the price is masked server-side before the wire payload
    // ships. See apps/preview/api/src/preview/strip.ts:maskOptionalPrice.
    showPreviewOptionalPrice: z.boolean().nullable().optional(),
    legs: z.unknown().optional(),
    details: z.unknown().optional(),
    // Virtual — merged server-side into `details.hideTitleInItinerary` so the
    // Title tab can flip it without replacing the whole `details` bag. The
    // traveller preview masks the title while it is true (`maskHiddenTitle`).
    hideTitle: z.boolean().optional(),
    // Virtual — a hotel's Already booked (T-407), merged server-side into
    // `details.plannerHotelBooking` (`booked`, or `quote` to book it with us).
    // Accommodation only; while booked it is out of every price.
    alreadyBooked: z.boolean().optional(),
    attachments: z.unknown().optional(),
    city: z.string().max(256).nullable().optional(),
    country: z.string().max(256).nullable().optional(),
    serviceType: z.string().max(64).nullable().optional(),
    // Phase 20f — Supplier tab on the item-edit page. All columns
    // already exist on `itinerary_item` (jsonb / text / numeric); the
    // patch passes the values through verbatim. `suppliers` + `tags`
    // stay `unknown` to mirror the rest of the item patch's JSONB
    // surface — the FE owns the shape (legacy `ProductSupplier[]` /
    // `ProductTag[]`). `contentSubcategory` is constrained to the
    // shared subcategory enum in the DB but accepted loosely here for
    // parity with `serviceType` / `transportType`.
    suppliers: z.unknown().optional(),
    tags: z.unknown().optional(),
    contentSubcategory: z.string().max(64).nullable().optional(),
    transportType: z.string().max(64).nullable().optional(),
    stars: z
      .number()
      .min(1)
      .max(5)
      .refine((v) => Number.isFinite(v) && (v * 2) % 1 === 0, {
        message: 'stars must be a multiple of 0.5 between 1 and 5',
      })
      .nullable()
      .optional(),
    // Phase 20g — Facilities tab. Matches Products' wire shape (UUID
    // string[] stored in the jsonb column; legacy `withFacility` join
    // hydrates titles on read). Empty array clears the column.
    facilities: z.array(z.uuid()).nullable().optional(),
    // [longitude, latitude] tuple — server writes the PostGIS Point
    // column via ST_SetSRID(ST_MakePoint(lng, lat), 4326). Edited
    // from the item-detail page's Location & Time tab. Denormalised
    // from `legs[0].locus.locations[0]` so PostGIS spatial queries
    // (custom-region containment, distance ranking) keep working.
    locationPoint: z.tuple([z.number(), z.number()]).nullable().optional(),
  })
  .strict()
  .refine((p) => Object.keys(p).length > 0, {
    message: 'Patch must contain at least one field',
  });

export type ItineraryItemPatch = z.infer<typeof itineraryItemPatchSchema>;

export const updateItineraryItemInput = z.object({
  id: z.uuid(),
  patch: itineraryItemPatchSchema,
});

export type UpdateItineraryItemInput = z.infer<typeof updateItineraryItemInput>;

export const deleteItineraryItemInput = z.object({
  id: z.uuid(),
});

export type DeleteItineraryItemInput = z.infer<typeof deleteItineraryItemInput>;

// --- Transport Information report (legacy "Transport Information" modal /
// car-report XLS parity). Buffered XLSX export of every non-optional
// Transportation item in the itinerary, grouped into three sheets
// (Per day / Per supplier / Per city-region). Mirrors the legacy
// `GET /itinerary-items/export/excel/:id/report?serviceType=transport`
// projection. Result shape matches `exportItinerariesResultSchema` so the
// FE reuses the same base64→Blob download helper.
export const exportTransportReportInput = z.object({
  itineraryId: z.uuid(),
});

export type ExportTransportReportInput = z.infer<
  typeof exportTransportReportInput
>;

export const transportReportResultSchema = z.object({
  base64: z.string(),
  filename: z.string(),
  mime: z.literal(
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ),
});

export type TransportReportResult = z.infer<typeof transportReportResultSchema>;

/**
 * Train F — wholesale-set the tour-operator list on an itinerary item.
 * Server computes the added/removed diff against the current JSONB array
 * and fires the WhatsApp auto-scheduler post-commit.
 *
 * Empty list is valid (unassigns all operators). Cap of 20 mirrors the
 * legacy practical limit — more than that is a UI/data-modelling bug.
 */
export const setTourOperatorsInput = z.object({
  itineraryItemId: z.uuid(),
  tourOperatorIds: z.array(z.uuid()).max(20),
});

export type SetTourOperatorsInput = z.infer<typeof setTourOperatorsInput>;

export const setTourOperatorsResultSchema = z.object({
  itineraryItemId: z.uuid(),
  added: z.array(z.uuid()),
  removed: z.array(z.uuid()),
  current: z.array(z.uuid()),
});

export type SetTourOperatorsResult = z.infer<
  typeof setTourOperatorsResultSchema
>;

// Reverses a soft-delete: nulls `deletedOn` on the parent row and any
// subitems that were soft-deleted in the same cascade. Powers the Undo
// toast surfaced after `delete`. `subitemIds` is optional — when present,
// the service nulls those rows alongside the parent; when absent, only
// the parent row is restored.
export const restoreItineraryItemInput = z.object({
  id: z.uuid(),
  subitemIds: z.array(z.uuid()).max(64).optional(),
});

export type RestoreItineraryItemInput = z.infer<
  typeof restoreItineraryItemInput
>;

// The planner's "Removed" filter: services taken off a version, either
// deleted in the planner or rejected by the client / agent.
export const REMOVED_SERVICE_REASONS = ['deleted', 'rejected'] as const;

export type RemovedServiceReason = (typeof REMOVED_SERVICE_REASONS)[number];

export const listRemovedServicesInput = z.object({
  itineraryVersionId: z.uuid(),
});

export type RemovedService = {
  id: string;
  reason: RemovedServiceReason;
  // Stored localized title shape; the client resolves it to its language.
  title: unknown;
  startTime: string | null;
  endTime: string | null;
  deletedOn: string | null;
  city: string | null;
  country: string | null;
  contentCategory: string | null;
  contentSubcategory: string | null;
  thumbnail: string | null;
  attachments: unknown;
  isOptional: boolean;
  dayIndex: number;
};

// `dayIndex` / `startTime` let the consultant "duplicate to Day 4 at 10am" in
// one shot. Both optional — when omitted the copy lands on the same day/time
// as the source (consultant can drag it after). Subitems are intentionally
// NOT cloned (legacy behaviour); the new row starts empty.
export const duplicateItineraryItemInput = z.object({
  id: z.uuid(),
  dayIndex: z.number().int().min(0).max(364).optional(),
  startTime: hhmmSchema.nullable().optional(),
});

export type DuplicateItineraryItemInput = z.infer<
  typeof duplicateItineraryItemInput
>;

// --- Reorder (Phase 7) ---
//
// Server-authoritative reorder. The FE sends a batch of `(id, dayIndex)`
// targets; the server preserves each item's time-of-day and only changes
// the date. All moves commit in one transaction so the planner can't end up
// half-updated when one item fails validation. `position` is reserved for
// future within-day ordering (Phase 13 DnD); v1 ignores it and consultants
// still set within-day times via `update`.

export const itineraryItemMoveSchema = z.object({
  id: z.uuid(),
  dayIndex: z.number().int().min(0).max(364),
  position: z.number().int().min(0).optional(),
  // Time-of-day to land on, when the caller already knows it. Omitted, the
  // server keeps the row's own HH:MM by re-deriving it from the stored
  // timestamp — which the planner deliberately avoids, because that fallback
  // has shifted times across a TZ boundary. Senders that hold the displayed
  // time send it verbatim instead of trusting the round-trip.
  startTime: hhmmSchema.optional(),
});

export type ItineraryItemMove = z.infer<typeof itineraryItemMoveSchema>;

export const reorderItineraryItemsInput = z
  .object({
    itineraryVersionId: z.uuid(),
    moves: z.array(itineraryItemMoveSchema).min(1).max(365),
  })
  .refine(
    (input) => {
      const seen = new Set<string>();
      for (const m of input.moves) {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
      }
      return true;
    },
    { message: 'Duplicate item id in moves' }
  );

export type ReorderItineraryItemsInput = z.infer<
  typeof reorderItineraryItemsInput
>;

export const subitemTypeSchema = z.enum([
  'Transport',
  'Guide',
  'Ticket',
  'Other',
]);

export type SubitemType = z.infer<typeof subitemTypeSchema>;

// A subitem's default title is its category plus the parent's full title,
// and item titles carry no limit, so this must leave room for long ones.
export const SUBITEM_TITLE_MAX_LENGTH = 1000;

// Server picks only the type's whitelisted keys before persistence.
const subitemTypeFieldsSchema = z.record(z.string(), z.unknown()).optional();

export const createSubitemInput = z.object({
  parentId: z.uuid(),
  type: subitemTypeSchema,
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
  endTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
  title: z.string().max(SUBITEM_TITLE_MAX_LENGTH).nullable().optional(),
  notes: z.string().max(10_000).nullable().optional(),
  // Top-level columns the legacy renders directly on the row, not in
  // `details`. Optional — only set by per-type forms that surface them.
  serviceType: z.string().max(64).nullable().optional(),
  transportType: z.string().max(64).nullable().optional(),
  // XT-837: cost / sell VAT (org `vat_tax_setting` ids). Omitted = the org's
  // default VAT for this subitem type; null = no VAT.
  costVatId: z.uuid().nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
  // Unit cost. Stored as `SingleProductPrice` jsonb: `{ cost, price,
  // markup, netCost }`.
  price: z.number().nullable().optional(),
  // Explicit user-set markup % (planner sell-price editing). When provided it
  // overrides the org's default markup and is persisted as a user-set markup
  // (so later edits preserve it); omitted = derive the default markup.
  markup: z.number().nullable().optional(),
  // The selling price the consultant TYPED (item currency). When present it is
  // the DRIVER: persisted verbatim, with `markup` derived from it at the
  // engine's 8dp. Deriving the price from a 2dp `markup` instead is what turned
  // a typed 860 into 859.98 (XT-662). Absent = the org/stored markup drives and
  // the price is derived, where cents are correct.
  sellPrice: z.number().nullable().optional(),
  // Per-traveller-type prices for an Other/Ticket subitem sold Per person.
  // When present they are persisted VERBATIM as the `price.travellers[]` lines
  // (each type priced independently), instead of expanding the scalar `price`
  // onto every type. Head-counts stay on the item's `travellers` composition —
  // the engine multiplies each line by its type's count.
  travellerPrices: z
    .array(
      z.object({
        travellerId: z.uuid(),
        cost: z.number().nullable().optional(),
        price: z.number().nullable().optional(),
        markup: z.number().nullable().optional(),
      })
    )
    .nullable()
    .optional(),
  fields: subitemTypeFieldsSchema,
  // Top-level `attachments` jsonb (ProductAttachment[]) — photos/files on the
  // subitem row itself, not in `details` (legacy parity). Surfaced by the
  // Other form's photo field and the Ticket form's file upload.
  attachments: z.unknown().optional(),
  // Per-item traveller composition (Ticket, legacy `InlineTravellerSelection`).
  // Bare `[{ id, title, count }]`; the service converts it to the canonical
  // `{ people }` shape, flags `travellersManuallySet`, and re-seeds the
  // ticket's PAX price-line types from it.
  travellers: z.unknown().optional(),
  travellersManuallySet: z.boolean().optional(),
});

export type CreateSubitemInput = z.infer<typeof createSubitemInput>;

export const updateSubitemInput = z.object({
  id: z.uuid(),
  patch: z
    .object({
      title: z.string().max(SUBITEM_TITLE_MAX_LENGTH).nullable().optional(),
      startTime: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .nullable()
        .optional(),
      endTime: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .nullable()
        .optional(),
      notes: z.string().max(10_000).nullable().optional(),
      serviceType: z.string().max(64).nullable().optional(),
      transportType: z.string().max(64).nullable().optional(),
      // XT-837: cost / sell VAT. Omitted = leave untouched; null = no VAT.
      costVatId: z.uuid().nullable().optional(),
      priceVatId: z.uuid().nullable().optional(),
      price: z.number().nullable().optional(),
      // Explicit user-set markup % (planner sell-price editing) — overrides the
      // default/preserved markup against the new base price when provided.
      markup: z.number().nullable().optional(),
      // Typed selling price — the driver when present. See `createSubitemInput`.
      sellPrice: z.number().nullable().optional(),
      // Per-traveller-type prices for an Other/Ticket subitem sold Per person.
      // When present they are persisted VERBATIM as the `price.travellers[]` lines
      // (each type priced independently), instead of expanding the scalar `price`
      // onto every type. Head-counts stay on the item's `travellers` composition —
      // the engine multiplies each line by its type's count.
      travellerPrices: z
        .array(
          z.object({
            travellerId: z.uuid(),
            cost: z.number().nullable().optional(),
            price: z.number().nullable().optional(),
            markup: z.number().nullable().optional(),
          })
        )
        .nullable()
        .optional(),
      fields: subitemTypeFieldsSchema,
      attachments: z.unknown().optional(),
      // Per-item traveller composition (Ticket) — see `createSubitemInput`.
      travellers: z.unknown().optional(),
      travellersManuallySet: z.boolean().optional(),
    })
    .strict()
    .refine((p) => Object.keys(p).length > 0, {
      message: 'Patch must contain at least one field',
    }),
});

export type UpdateSubitemInput = z.infer<typeof updateSubitemInput>;

export const deleteSubitemInput = z.object({
  id: z.uuid(),
});

export type DeleteSubitemInput = z.infer<typeof deleteSubitemInput>;

// Re-parent an existing top-level item under `parentId` ("Add as subitem" /
// transform). Mirrors legacy's PATCH `{ parentId }`.
export const setItemParentInput = z.object({
  id: z.uuid(),
  parentId: z.uuid(),
});

export type SetItemParentInput = z.infer<typeof setItemParentInput>;

export const assignOptionalItemsInput = z.object({
  itemId: z.uuid(),
  parentIds: z.array(z.uuid()).max(50),
});

export type AssignOptionalItemsInput = z.infer<typeof assignOptionalItemsInput>;

export const removeOptionalItemInput = z.object({
  itemId: z.uuid(),
  parentId: z.uuid(),
});

export type RemoveOptionalItemInput = z.infer<typeof removeOptionalItemInput>;

export const listItineraryVersionsInput = z.object({
  itineraryId: z.uuid(),
});

export type ListItineraryVersionsInput = z.infer<
  typeof listItineraryVersionsInput
>;

export const itineraryVersionSummarySchema = z.object({
  id: z.uuid(),
  itineraryId: z.uuid(),
  versionTitle: z.string(),
  startTime: z.string().nullable(),
  finishTime: z.string().nullable(),
  durationDays: z.number().int().nullable(),
  modifiedOn: z.string().nullable(),
  createdOn: z.string().nullable(),
  isActive: z.boolean(),
});

export type ItineraryVersionSummary = z.infer<
  typeof itineraryVersionSummarySchema
>;

export const createItineraryVersionInput = z.object({
  itineraryId: z.uuid(),
  versionTitle: z.string().min(1).max(256),
  fromVersionId: z.uuid().optional(),
});

export type CreateItineraryVersionInput = z.infer<
  typeof createItineraryVersionInput
>;

export const setActiveItineraryVersionInput = z.object({
  itineraryId: z.uuid(),
  versionId: z.uuid(),
});

export type SetActiveItineraryVersionInput = z.infer<
  typeof setActiveItineraryVersionInput
>;

export const renameItineraryVersionInput = z.object({
  id: z.uuid(),
  versionTitle: z.string().min(1).max(256),
});

export type RenameItineraryVersionInput = z.infer<
  typeof renameItineraryVersionInput
>;

export const itineraryPatchSchema = z
  .object({
    title: z.string().min(1).max(256).nullable().optional(),
    isLocked: z.boolean().optional(),
    // XT-692 — storefront title per language. Null clears it back to the
    // derived default (the plain title in the itinerary's language).
    cmsTitle: itineraryCmsTitleSchema.nullable().optional(),
    // FSM-checked server-side against the row's salesStage.
    statusId: z.uuid().optional(),
    note: z.string().max(10_000).nullable().optional(),
    privateNotes: z.string().max(10_000).nullable().optional(),
    allSuppliersNotes: z.string().max(10_000).nullable().optional(),
    // Reason for moving the itinerary into a CANCELLED status (legacy
    // parity). Sent alongside `statusId` from the cancel-reason modal.
    cancellationReason: z.string().max(10_000).nullable().optional(),
    tags: z.unknown().optional(),
    languageCode: z.string().min(2).max(8).nullable().optional(),
    // Main country of travel. Stamped at create and editable from the settings
    // page (XT-600): the schema is `.strict()`, so before it was listed here
    // the Country select was silently dropped from every patch — the save
    // reported success while the value reverted.
    country: z.string().min(1).max(128).nullable().optional(),
    // XT-158: NOT NULL columns — omit to leave unchanged, never send null.
    hourFormat: hourFormatSchema.optional(),
    dateFormat: dateFormatSchema.optional(),
    distanceUnit: distanceUnitSchema.optional(),
    // Canonical 3-letter currency code used by the pricing engine.
    // The legacy form binds its `currency` field to this column.
    currency: z.string().length(3).nullable().optional(),
    displayCurrency: z.string().length(3).nullable().optional(),
    contactId: z.uuid().nullable().optional(),
    accountId: z.uuid().nullable().optional(),
    appearanceId: z.uuid().nullable().optional(),
    regionalVideoId: z.uuid().nullable().optional(),
    // Allows the consultant to override the server-stamped soldOn from
    // the settings page. UTC ISO timestamp.
    soldOn: z.string().nullable().optional(),
    // Customer-mode preview budget breakdown surface. Flipped from the
    // preview Navbar's Budget Breakdown toggle (consultant only).
    showBudgetBreakdownInPreview: z.boolean().optional(),
  })
  .strict()
  .refine((p) => Object.keys(p).length > 0, {
    message: 'Patch must contain at least one field',
  });

export type ItineraryPatch = z.infer<typeof itineraryPatchSchema>;

export const updateItineraryInput = z.object({
  id: z.uuid(),
  patch: itineraryPatchSchema,
});

export type UpdateItineraryInput = z.infer<typeof updateItineraryInput>;

// --- Itinerary chaining ---

// Sets (or clears, when `nextItineraryId` is null) the itinerary's forward
// chain link. The service maintains the back-pointer on the neighbour
// bidirectionally. `previous` is never set directly — it is always a
// consequence of some other itinerary's `next`.
export const setItineraryChainInput = z.object({
  id: z.uuid(),
  nextItineraryId: z.uuid().nullable(),
});

export type SetItineraryChainInput = z.infer<typeof setItineraryChainInput>;

// Reference-search for the "set next itinerary" combobox. Org-scoped by
// default; matches against `itinerary_ref`, title and OptiGest number.
export const searchNextItinerariesInput = z.object({
  reference: z.string().trim().min(2),
  limit: z.number().int().positive().max(50).default(10),
  // The itinerary doing the searching — excluded from results so a
  // consultant can't pick it as its own `next` (the server-side self-link
  // guard still rejects it, but filtering keeps it out of the picker).
  excludeId: z.uuid().optional(),
  // Search every tenant instead of just the caller's org (XT-710). Opt-in
  // per call site, NOT the default: a multi-country trip is chained across
  // partner DMCs (PT > IT > GR), but the builder and incident pickers link
  // within one org and must stay scoped.
  crossOrganization: z.boolean().default(false),
});

export type SearchNextItinerariesInput = z.infer<
  typeof searchNextItinerariesInput
>;

// Resolve a set of itinerary ids to their reference/title — used to pre-seed
// the incident "Link an itinerary" picker with itineraries detected on the
// current page. Org-scoped server-side; capped to a sane batch size.
export const itineraryReferencesByIdsInput = z.object({
  ids: z.array(z.uuid()).max(100),
});

export type ItineraryReferencesByIdsInput = z.infer<
  typeof itineraryReferencesByIdsInput
>;

export const itineraryReferenceMatchSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  // OptiGest number of the ACTIVE version (`itinerary_version
  // .accounting_reference`). Returned so a picker can both match on it and
  // show it — a result found by OptiGest is otherwise indistinguishable from
  // one found by reference. Nullish: not every itinerary has one.
  accountingReference: z.string().nullish(),
});

export type ItineraryReferenceMatch = z.infer<
  typeof itineraryReferenceMatchSchema
>;

// --- Row-action inputs (legacy itineraries-list table parity) ---

export const deleteItineraryInput = z.object({
  id: z.uuid(),
});

export type DeleteItineraryInput = z.infer<typeof deleteItineraryInput>;

// V1 shallow clone: copies the itinerary row + active version row only.
// Items, subitems, and price lines are NOT cloned (follow-up issue tracks
// full legacy parity). `targetCustomerId` / `targetOwnerId` override the
// source values when present.
export const duplicateItineraryInput = z.object({
  id: z.uuid(),
  // The new itinerary's title. Prefilled in the modal from the source title
  // and editable; falls back to the legacy `[Copy] <source>` title when not
  // provided (older callers / programmatic duplicates).
  title: z.string().trim().min(1).max(256).optional(),
  // The new itinerary's currency (3-letter code). Prefilled from the source
  // and editable; falls back to the source currency when not provided.
  currency: z.string().trim().length(3).optional(),
  targetCustomerId: z.uuid().nullable().optional(),
  targetOwnerId: z.uuid().nullable().optional(),
});

export type DuplicateItineraryInput = z.infer<typeof duplicateItineraryInput>;

export const setItineraryDisabledInput = z.object({
  id: z.uuid(),
  disabled: z.boolean(),
});

export type SetItineraryDisabledInput = z.infer<
  typeof setItineraryDisabledInput
>;

export const updateItineraryNoteInput = z.object({
  id: z.uuid(),
  note: z.string().max(10_000).nullable(),
});

export type UpdateItineraryNoteInput = z.infer<typeof updateItineraryNoteInput>;

export const updateItineraryOwnerInput = z.object({
  id: z.uuid(),
  ownerId: z.uuid(),
});

export type UpdateItineraryOwnerInput = z.infer<
  typeof updateItineraryOwnerInput
>;

export const updateItineraryCollaboratorsInput = z.object({
  id: z.uuid(),
  collaboratorAccountIds: z.array(z.uuid()).max(50),
});

export type UpdateItineraryCollaboratorsInput = z.infer<
  typeof updateItineraryCollaboratorsInput
>;

export const updateItineraryStatusInput = z.object({
  id: z.uuid(),
  statusId: z.uuid(),
  // Required by the UIs when moving to a Cancelled-stage status (legacy
  // parity); persisted to `cancellation_reason`.
  cancellationReason: z.string().max(10_000).nullable().optional(),
});

export type UpdateItineraryStatusInput = z.infer<
  typeof updateItineraryStatusInput
>;

// Admin-only itinerary status (legacy parity). Hidden from `listStatuses`
// for non-admins and rejected if a non-admin tries to assign it. The name
// is matched exactly (spaces around the dash), mirroring the legacy
// `EXCLUDED_APPROVED_ITINERARY_STATUS` constant.
// The plain 'Excluded' status (salesStage 0). Not admin-gated, but in
// `listStatuses` it sorts just after its same-stage peers and before
// 'Excluded - Approved' (legacy `itinerary-status.service` ordering).
export const EXCLUDED_ITINERARY_STATUS = 'Excluded';

export const EXCLUDED_APPROVED_ITINERARY_STATUS = 'Excluded - Approved';

// The tag the AI agent writes on a rehearsal (a TEST write, v7's `TESTE`).
// A rehearsal is never sales work: the sales lists and reports leave it out,
// and only an operator's clean-up removes it (XTAPW-373, XTAPW-374). Matched
// exactly — the platform writes it; a person who removes it promotes the file.
export const REHEARSAL_TAG = 'TESTE';

// --- Status *definition* CRUD (the org-scoped catalog of statuses an
// itinerary can be assigned). Distinct from `updateItineraryStatusInput`
// above, which assigns an existing status to an itinerary. Gated behind
// `company_settings` (see itineraries router). `salesStage` is the FSM
// numeric (nullable — uncategorised statuses sort last).
const hexColor = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Invalid hex color');

export const createItineraryStatusInput = z.object({
  status: z.string().trim().min(1, 'Status name is required').max(100),
  color: hexColor,
  salesStage: z.number().int().nullable(),
  // XT-521 — opt-in per status; a new status never locks unless asked to.
  activeVersionLocked: z.boolean().default(false),
  // Null keeps the built-in Traveller Preview rules for this status.
  travellerPreview: travellerPreviewAccessSchema.nullable().default(null),
  // Null keeps the name-based process detection for this status.
  processStep: itineraryProcessStepSchema.nullable().default(null),
  processSteps: itineraryProcessTargetsSchema.optional(),
});

export type CreateItineraryStatusInput = z.infer<
  typeof createItineraryStatusInput
>;

// The *pre-parse* shape: `activeVersionLocked` carries `.default(false)`, so it
// is optional going in and always present coming out. Service methods take this
// variant — the router hands them already-parsed input (default applied), while
// direct callers may omit the flag and let the column default (false) stand.
export type CreateItineraryStatusArgs = z.input<
  typeof createItineraryStatusInput
>;

export const updateItineraryStatusDefinitionInput = z.object({
  id: z.uuid(),
  status: z
    .string()
    .trim()
    .min(1, 'Status name is required')
    .max(100)
    .optional(),
  color: hexColor.optional(),
  salesStage: z.number().int().nullable().optional(),
  activeVersionLocked: z.boolean().optional(),
  travellerPreview: travellerPreviewAccessSchema.nullable().optional(),
  processStep: itineraryProcessStepSchema.nullable().optional(),
  processSteps: itineraryProcessTargetsSchema.optional(),
});

export type UpdateItineraryStatusDefinitionInput = z.infer<
  typeof updateItineraryStatusDefinitionInput
>;

// Hard-delete a status definition. The service rejects the call when the
// status still has itineraries assigned (the FE also disables the button),
// so this only ever removes an empty status. Gated behind `company_settings`.
export const deleteItineraryStatusInput = z.object({
  id: z.uuid(),
});

export type DeleteItineraryStatusInput = z.infer<
  typeof deleteItineraryStatusInput
>;

// Bulk-move every non-deleted itinerary from one status to another (the
// mechanism to empty a status before deleting it). Rejected when the two
// ids are equal. Gated behind `company_settings`.
export const reassignItineraryStatusInput = z
  .object({
    fromStatusId: z.uuid(),
    toStatusId: z.uuid(),
  })
  .refine((i) => i.fromStatusId !== i.toStatusId, {
    message: 'Source and target status must differ',
    path: ['toStatusId'],
  });

export type ReassignItineraryStatusInput = z.infer<
  typeof reassignItineraryStatusInput
>;

export const updateItineraryTravellersInput = z.object({
  id: z.uuid(),
  travellers: z.unknown(),
  // Cascade skips rows with `travellersManuallySet = true`.
  applyToExistingItems: z.boolean().default(false),
});

export type UpdateItineraryTravellersInput = z.infer<
  typeof updateItineraryTravellersInput
>;

export const toggleHolidayInput = z.object({
  id: z.uuid(),
  holidayDate: z.iso.date(),
  hidden: z.boolean(),
});

export type ToggleHolidayInput = z.infer<typeof toggleHolidayInput>;

// Specific→undated keeps durationDays; undated→specific requires both anchors.
export const updateItineraryDateTypeInput = z
  .object({
    id: z.uuid(),
    undated: z.boolean(),
    startTime: z.iso.datetime().nullable().optional(),
    finishTime: z.iso.datetime().nullable().optional(),
    durationDays: z.number().int().min(1).max(365).optional(),
  })
  .refine(
    (i) => {
      if (i.undated === false) {
        return i.startTime != null && i.finishTime != null;
      }
      return true;
    },
    {
      message: 'Specific date mode requires both startTime and finishTime',
    }
  );

export type UpdateItineraryDateTypeInput = z.infer<
  typeof updateItineraryDateTypeInput
>;

// Phase 19b — create flow. Wraps the union of fields the legacy
// `POST /itineraries` endpoint accepts: itinerary-row columns plus
// the initial version's columns (start/finish/duration/travellers/
// budget/paymentZone/T&C/accountingReference/contacts). Required
// `contactId` + `termsAndConditionsId` mirror the legacy form's
// hard-required validation; the rest is optional and falls back to
// org defaults (currency, language, appearance, paymentZone) inside
// the service.
export const createItineraryInput = z
  .object({
    // Itinerary-row fields
    title: z.string().min(1).max(256).nullable().optional(),
    contactId: z.uuid(),
    accountId: z.uuid().nullable().optional(),
    country: z.string().max(128).nullable().optional(),
    languageCode: z.string().min(2).max(8).nullable().optional(),
    currency: z.string().length(3).nullable().optional(),
    appearanceId: z.uuid().nullable().optional(),
    regionalVideoId: z.uuid().nullable().optional(),
    // XT-158: optional document format settings; default at the DB when unset.
    hourFormat: hourFormatSchema.optional(),
    dateFormat: dateFormatSchema.optional(),
    distanceUnit: distanceUnitSchema.optional(),
    tags: z.array(z.string()).optional(),
    note: z.string().max(10_000).nullable().optional(),
    privateNotes: z.string().max(10_000).nullable().optional(),
    allSuppliersNotes: z.string().max(10_000).nullable().optional(),
    undated: z.boolean().optional(),

    // Version-row fields
    startTime: z.iso.datetime().nullable().optional(),
    finishTime: z.iso.datetime().nullable().optional(),
    durationDays: z.number().int().min(1).max(365).optional(),
    travellers: z.unknown().optional(),
    travellersContactInfo: z.array(z.string()).nullable().optional(),
    budget: z.union([z.string(), z.number(), z.null()]).optional(),
    paymentZoneId: z.uuid().nullable().optional(),
    termsAndConditionsId: z.uuid(),
    accountingReference: z.string().max(256).nullable().optional(),
    // Optional initial status. Defaults server-side to the org's
    // lowest-positive-sales-stage status (typically "Draft") so the
    // new row passes the itineraries-list `salesStage > 0` filter.
    statusId: z.uuid().nullable().optional(),
  })
  .refine(
    (i) => {
      if (i.undated === false) {
        return i.startTime != null && i.finishTime != null;
      }
      return true;
    },
    {
      message: 'Specific date mode requires both startTime and finishTime',
    }
  );

export type CreateItineraryInput = z.infer<typeof createItineraryInput>;

export const updateItineraryVersionInput = z.object({
  id: z.uuid(),
  patch: z
    .object({
      versionTitle: z.string().min(1).max(256).optional(),
      paymentZoneId: z.uuid().nullable().optional(),
      termsAndConditionsId: z.uuid().optional(),
      accountingReference: z.string().max(256).nullable().optional(),
      priorityLevel: z.number().nullable().optional(),
      attentionCategory: z.number().nullable().optional(),
      // Numeric budget on the version; stored as a Drizzle `numeric` (string).
      // Accept number-or-string-or-null; coerce in the service.
      budget: z.union([z.string(), z.number(), z.null()]).optional(),
      // Legacy `string[]` of `name:email:phone` triples for the
      // Travelers section repeater.
      travellersContactInfo: z.array(z.string()).nullable().optional(),
      // Presentation-image gallery (ProductAttachment[] jsonb). Written
      // by the settings Photos tab; the preview hero reads IMAGE rows
      // from here. `unknown` keeps the FE owning the shape, matching the
      // item patch's `attachments` and the version's create-time field.
      attachments: z.unknown().optional(),
    })
    .strict()
    .refine((p) => Object.keys(p).length > 0, {
      message: 'Patch must contain at least one field',
    }),
});

export type UpdateItineraryVersionInput = z.infer<
  typeof updateItineraryVersionInput
>;

export const chapterSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1).max(256),
  country: z.string().nullable(),
  // Custom-region UUID (`organizations.custom_region.id`) — matches the
  // legacy planner, where a chapter's region is an org-defined polygon and
  // the active chapter feeds this id into the product search as
  // `customRegionIds` (ST_Contains against the region boundary). The chapter
  // editor binds the Region dropdown to `customRegions.findAll`. Typed as a
  // bare string (not `z.uuid()`) so chapters saved under the earlier
  // region-NAME shape still round-trip; non-uuid legacy values simply won't
  // match any region and no-op the product filter until re-picked.
  regionId: z.string().max(128).nullable(),
  travellers: z.unknown(),
  startDate: z.iso.date(),
  endDate: z.iso.date(),
  color: z.string().max(32).nullable(),
  order: z.number().int().min(0),
});

export type Chapter = z.infer<typeof chapterSchema>;

export const updateItineraryChaptersInput = z.object({
  id: z.uuid(),
  chapters: z.array(chapterSchema).max(50),
});

export type UpdateItineraryChaptersInput = z.infer<
  typeof updateItineraryChaptersInput
>;

// ISO-4217 → units per 1 unit of the version's base currency.
const exchangeRatesSchema = z
  .record(
    z
      .string()
      .length(3)
      .regex(/^[A-Z]{3}$/),
    z.number().gt(0)
  )
  .refine((rates) => Object.keys(rates).length <= 200, {
    message: 'exchangeRates must contain at most 200 entries',
  });

export const updateExchangeRatesInput = z.object({
  versionId: z.uuid(),
  rates: exchangeRatesSchema,
});

export type UpdateExchangeRatesInput = z.infer<typeof updateExchangeRatesInput>;

export const itineraryPriceLineSchema = z.object({
  id: z.uuid(),
  itineraryVersionId: z.uuid().nullable(),
  title: z.unknown(),
  notes: z.string().nullable(),
  price: z.unknown(),
  pricingType: z.string().nullable(),
  pricingUnits: z.number().int().nullable(),
  isGross: z.boolean().nullable(),
  currency: z.string().nullable(),
  priceVatId: z.uuid().nullable(),
  costVatId: z.uuid().nullable(),
  attachments: z.unknown(),
  createdOn: z.string(),
});

export type ItineraryPriceLine = z.infer<typeof itineraryPriceLineSchema>;

export const listItineraryPriceLinesInput = z.object({
  itineraryVersionId: z.uuid(),
});

export type ListItineraryPriceLinesInput = z.infer<
  typeof listItineraryPriceLinesInput
>;

// jsonb (`{ en, fr, ... }` multilingual); FE owns rendering.
const titleSchema = z.unknown();

export const createItineraryPriceLineInput = z.object({
  itineraryVersionId: z.uuid(),
  title: titleSchema,
  notes: z.string().max(10_000).nullable().optional(),
  price: z.unknown().optional(),
  pricingType: z.string().max(64).nullable().optional(),
  pricingUnits: z.number().int().nullable().optional(),
  isGross: z.boolean().nullable().optional(),
  currency: z.string().length(3).nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
  costVatId: z.uuid().nullable().optional(),
  attachments: z.unknown().optional(),
});

export type CreateItineraryPriceLineInput = z.infer<
  typeof createItineraryPriceLineInput
>;

export const updateItineraryPriceLineInput = z.object({
  id: z.uuid(),
  patch: z
    .object({
      title: titleSchema.optional(),
      notes: z.string().max(10_000).nullable().optional(),
      price: z.unknown().optional(),
      pricingType: z.string().max(64).nullable().optional(),
      pricingUnits: z.number().int().nullable().optional(),
      isGross: z.boolean().nullable().optional(),
      currency: z.string().length(3).nullable().optional(),
      priceVatId: z.uuid().nullable().optional(),
      costVatId: z.uuid().nullable().optional(),
      attachments: z.unknown().optional(),
    })
    .strict()
    .refine((p) => Object.keys(p).length > 0, {
      message: 'Patch must contain at least one field',
    }),
});

export type UpdateItineraryPriceLineInput = z.infer<
  typeof updateItineraryPriceLineInput
>;

export const deleteItineraryPriceLineInput = z.object({
  id: z.uuid(),
});

export type DeleteItineraryPriceLineInput = z.infer<
  typeof deleteItineraryPriceLineInput
>;

export const itineraryPaymentSchema = z.object({
  id: z.uuid(),
  itineraryId: z.uuid(),
  customerId: z.uuid(),
  accountId: z.uuid(),
  status: z.string(),
  method: z.string(),
  billingType: z.string(),
  billingAmount: z.string(),
  billingAmountEur: z.string().nullable(),
  billingAmountUsd: z.string().nullable(),
  accountingReference: z.string().nullable(),
  cardLast4: z.string().nullable(),
  internalNote: z.string().nullable(),
  paidOn: z.string().nullable(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});

export type ItineraryPayment = z.infer<typeof itineraryPaymentSchema>;

export const listItineraryPaymentsInput = z.object({
  itineraryId: z.uuid(),
});

export type ListItineraryPaymentsInput = z.infer<
  typeof listItineraryPaymentsInput
>;

export const addItineraryDayInput = z.object({
  versionId: z.uuid(),
  atIndex: z.number().int().min(0).max(364),
  position: z.enum(['before', 'after']),
  // `shift-items` (default, when omitted) splices at `atIndex` +
  // `position` and shifts every later item forward by 1 day.
  // `extend-calendar` ignores `atIndex` / `position`, always prepends
  // at day 0, and moves the version's `startTime` back by 1 day
  // instead — items keep their literal dates. Matches legacy `addDay`
  // vs `addDayBefore`.
  mode: z.enum(['shift-items', 'extend-calendar']).optional(),
});

export type AddItineraryDayInput = z.infer<typeof addItineraryDayInput>;

export const removeItineraryDayInput = z.object({
  versionId: z.uuid(),
  atIndex: z.number().int().min(0).max(364),
});

export type RemoveItineraryDayInput = z.infer<typeof removeItineraryDayInput>;

export const editItineraryDayTitleInput = z.object({
  versionId: z.uuid(),
  index: z.number().int().min(0).max(364),
  title: z.string().max(256).nullable(),
});

export type EditItineraryDayTitleInput = z.infer<
  typeof editItineraryDayTitleInput
>;

export const editItineraryDayImageInput = z.object({
  versionId: z.uuid(),
  index: z.number().int().min(0).max(364),
  // An attachment fileKey (legacy `dayImages` parity) — NOT necessarily a URL,
  // so this is a plain string, not `z.url()`. `null` clears the day image.
  imageUrl: z.string().nullable(),
});

export type EditItineraryDayImageInput = z.infer<
  typeof editItineraryDayImageInput
>;

// Writes `day_titles[index].description` (preserving title + AI flags).
// `descriptionAiGenerated` defaults to false — the AI generator that flips
// this to true is a separate workflow.
export const editItineraryDayDescriptionInput = z.object({
  versionId: z.uuid(),
  index: z.number().int().min(0).max(364),
  description: z.string().max(10_000).nullable(),
});

export type EditItineraryDayDescriptionInput = z.infer<
  typeof editItineraryDayDescriptionInput
>;

// Generates AI titles (and optional descriptions) for every non-locked day
// of the active version. Locked days = entries where `aiGenerated === false`
// and `title.trim().length > 0`; those are preserved verbatim. Days where
// the user has no manual title are regenerated, and the resulting title +
// optional description are written back with `aiGenerated: true`.
export const generateAiTitlesInput = z.object({
  // Itinerary id — server resolves the active version internally. Mirrors
  // legacy NestJS contract `PATCH /:id/generate-ai-titles`. Avoids the
  // customer payload needing to expose `activeVersionId`.
  itineraryId: z.uuid(),
  // Legacy parity: `false` clears every entry where `aiGenerated === true`
  // (titles + descriptions reset, flags flipped off). Default `true` runs
  // the AI regeneration path. Manual titles (`aiGenerated === false` with
  // non-empty title) are preserved in BOTH modes.
  generateAiTitles: z.boolean().optional(),
  promptStyle: z.enum(['editorial', 'poetic']).optional(),
  includeDescription: z.boolean().optional(),
  language: z.string().min(1).max(64).optional(),
});

export type GenerateAiTitlesInput = z.infer<typeof generateAiTitlesInput>;

export const generateAiTitlesOutput = z.object({
  versionId: z.uuid(),
  dayTitles: z.array(z.string()),
  dayDescriptions: z.array(z.string()),
  dayTitlesAiGenerated: z.array(z.boolean()),
  hasAiGeneratedTitles: z.boolean(),
});

export type GenerateAiTitlesOutput = z.infer<typeof generateAiTitlesOutput>;

// --- Preview publish window mutations ---

// Publishes the customer preview (legacy `POST /:id/publish-preview`).
// `unpublishTime` is the optional scheduled expiry — the worker that flips
// `disabled=true` on expiry is out of scope; the column ships now for
// bookkeeping + display. Side effects: `previewPublishTime = now`,
// `previewUnpublishTime = unpublishTime ?? null`, `disabled = false`.
export const publishPreviewInput = z.object({
  id: z.uuid(),
  unpublishTime: z.iso.datetime().nullable().optional(),
});

export type PublishPreviewInput = z.infer<typeof publishPreviewInput>;

// Unpublishes the customer preview (legacy `POST /:id/unpublish-preview`).
// Side effects: clears both timestamps + `disabled = true` so the preview
// read returns NOT_FOUND.
export const unpublishPreviewInput = z.object({
  id: z.uuid(),
});

export type UnpublishPreviewInput = z.infer<typeof unpublishPreviewInput>;

// Reschedules the expiry without re-publishing. Rejects if the preview
// isn't currently published.
export const reschedulePreviewUnpublishInput = z.object({
  id: z.uuid(),
  unpublishTime: z.iso.datetime(),
});

export type ReschedulePreviewUnpublishInput = z.infer<
  typeof reschedulePreviewUnpublishInput
>;
